/**
 * Spec 037: what the company's staff know and learn — how an instruction, a lesson and a staff
 * member's marks read. Plain functions: the tests run this module as it is.
 */
import type { LessonOut, UnitMemberOut } from "@/api/units";

export const LESSON_LIMIT = 20;
/** At this many active lessons the head is told to rewrite the instruction. */
export const LESSON_THRESHOLD = 15;
/** The instruction is the card's setup with these headings; «Инструменты» joins them when the
 * agent works with tools of its own. */
export const SECTIONS = ["Обязанности", "Что сдаю и кому", "Проверить перед сдачей", "Чего не делаю"];

export type Mark = { label: string; tone: "needs" | "waiting" };

/** A staff member's marks in the tree: an incomplete instruction, and its lessons. */
export function memberMarks(m: Pick<UnitMemberOut, "instruction_missing" | "lessons">): Mark[] {
  const marks: Mark[] = [];
  if ((m.instruction_missing ?? []).length) marks.push({ label: "инструкция неполная", tone: "needs" });
  const n = m.lessons ?? 0;
  if (n > 0) marks.push({ label: `уроков: ${n} из ${LESSON_LIMIT}`, tone: n >= LESSON_THRESHOLD ? "needs" : "waiting" });
  return marks;
}

/** «Не хватает разделов: …», or nothing when the instruction is whole. */
export function missingLine(missing: string[] | undefined): string {
  if (!missing || !missing.length) return "";
  return "Не хватает разделов: " + missing.map((s) => `«${s}»`).join(", ");
}

/** Who wrote a lesson, as people read it. */
export function lessonBy(l: Pick<LessonOut, "source" | "by" | "via_agent">): string {
  if (l.source === "self") return "сам себе";
  const name = l.by?.name ?? "—";
  if (l.source === "head") return `руководитель ${name}`;
  return l.via_agent ? `${name} через агента` : name;
}

/** Where a lesson stands: repeated, taken into an instruction, deleted. */
export function lessonState(l: Pick<LessonOut, "state" | "repeats" | "in_version">): string {
  if (l.state === "deleted") return "удалён";
  if (l.state === "archived") return l.in_version ? `вписан в версию ${l.in_version}` : "в архиве";
  return l.repeats ? `повторялся: ${l.repeats}` : "действует";
}
